import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Lock, User } from 'lucide-react';
import { toast } from 'sonner';

const SettingsEditor = ({ token }) => {
  const { changePassword, changeUsername, user } = useAuth();
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loadingPassword, setLoadingPassword] = useState(false);

  // Username change state
  const [newUsername, setNewUsername] = useState('');
  const [usernamePassword, setUsernamePassword] = useState('');
  const [loadingUsername, setLoadingUsername] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoadingPassword(true);
    const result = await changePassword(currentPassword, newPassword);

    if (result.success) {
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      toast.error(result.error);
    }

    setLoadingPassword(false);
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();

    if (!newUsername || newUsername.trim().length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    if (newUsername === user?.username) {
      toast.error('New username must be different from current username');
      return;
    }

    setLoadingUsername(true);
    const result = await changeUsername(newUsername, usernamePassword);

    if (result.success) {
      toast.success('Username changed successfully! Please login again with your new username.');
      setNewUsername('');
      setUsernamePassword('');
    } else {
      toast.error(result.error);
    }

    setLoadingUsername(false);
  };

  return (
    <div className="bg-white rounded-lg p-6 space-y-8">
      <h2 className="text-2xl font-bold text-slate-900">Settings</h2>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Change Username Section */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <User className="w-6 h-6 text-slate-600" />
            <h3 className="text-lg font-semibold">Change Username</h3>
          </div>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Current Username:</strong> {user?.username}
            </p>
          </div>

          <form onSubmit={handleUsernameChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">New Username</label>
              <Input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Enter new username"
                required
                minLength={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirm Password</label>
              <Input
                type="password"
                value={usernamePassword}
                onChange={(e) => setUsernamePassword(e.target.value)}
                placeholder="Enter your password to confirm"
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loadingUsername}
            >
              {loadingUsername ? 'Changing Username...' : 'Change Username'}
            </Button>
          </form>
        </div>

        {/* Change Password Section */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-6 h-6 text-slate-600" />
            <h3 className="text-lg font-semibold">Change Password</h3>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Current Password</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">New Password</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirm New Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loadingPassword}
            >
              {loadingPassword ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SettingsEditor;
